import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, RefreshCw, TrendingUp, Quote, ShieldAlert, Inbox } from 'lucide-react';
import supabase from '../../lib/supabaseClient';
import { fetchWithCsrf } from '../../lib/fetchWithCsrf';
import { card, btnPrimary } from './theme';

/**
 * Digest — the weekly rollup. This is Hunter's actual success metric: cite-bait
 * topic candidates generated from real buyer language, ranked by recurrence,
 * not a count of comments posted. One stored row per week (built by the Monday
 * cron, or on demand here); past weeks stay visible.
 */
export default function Digest() {
  const [digests, setDigests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchDigests = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('hunter_weekly_digests')
      .select('*')
      .order('week_start', { ascending: false })
      .limit(20);
    if (!error && data) setDigests(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchDigests();
  }, [fetchDigests]);

  const generateNow = async () => {
    setGenerating(true);
    setMsg(null);
    try {
      const res = await fetchWithCsrf('/api/admin/hunter/digest/run-now', { method: 'POST' });
      if (res.ok) {
        setMsg({ type: 'ok', text: 'Digest generated.' });
        fetchDigests();
      } else if (res.status === 409) {
        setMsg({ type: 'ok', text: 'A digest is already generating.' });
      } else {
        const body = await res.json().catch(() => ({}));
        setMsg({ type: 'err', text: body.error || `Failed to generate (${res.status}).` });
      }
    } catch (_) {
      setMsg({ type: 'err', text: 'Could not reach the server.' });
    } finally {
      setGenerating(false);
      setTimeout(() => setMsg(null), 6000);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          Built weekly (Monday). Top cite-bait topic candidates, exact phrases worth reusing, and
          topics to avoid as duplicates of a shipped post.
        </p>
        <div className="flex flex-col items-end gap-1">
          <button className={btnPrimary} disabled={generating} onClick={generateNow}>
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Generate now
          </button>
          {msg && (
            <span className={`text-xs ${msg.type === 'err' ? 'text-rose-600' : 'text-gray-500'}`}>{msg.text}</span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : digests.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Inbox className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-sm font-medium text-gray-700">No digests yet</p>
          <p className="text-xs text-gray-500 mt-1">
            Generate one now, or wait for the Monday run.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {digests.map((d) => <DigestCard key={d.id} digest={d} />)}
        </div>
      )}
    </div>
  );
}

function DigestCard({ digest }) {
  const topics = digest.top_topics || [];
  const phrases = digest.phrases_to_reuse || [];
  const avoid = digest.topics_to_avoid || [];

  return (
    <div className={`${card} p-5`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-800">
          {digest.week_start} — {digest.week_end}
        </h3>
      </div>

      {topics.length === 0 && phrases.length === 0 && avoid.length === 0 ? (
        <p className="text-sm text-gray-400">No cite-bait hits this week.</p>
      ) : (
        <div className="space-y-4">
          {topics.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-indigo-700 mb-2">
                <TrendingUp className="w-3.5 h-3.5" />
                Top topic candidates
              </div>
              <ol className="space-y-2">
                {topics.map((t, i) => (
                  <li key={i} className="text-sm text-gray-700">
                    <span className="font-medium text-gray-900">{i + 1}. {t.title}</span>
                    {typeof t.recurrence === 'number' && (
                      <span className="text-xs text-gray-400 ml-1.5">({t.recurrence}x)</span>
                    )}
                    {Array.isArray(t.phrases) && t.phrases.length > 0 && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {t.phrases.map((p, j) => <span key={j} className="italic">"{p}"{j < t.phrases.length - 1 ? ', ' : ''}</span>)}
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {phrases.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2">
                <Quote className="w-3.5 h-3.5" />
                Phrases to reuse
              </div>
              <div className="flex flex-wrap gap-1.5">
                {phrases.map((p, i) => (
                  <span key={i} className="px-2 py-0.5 text-xs bg-gray-50 border border-gray-200 rounded-full text-gray-600">
                    "{p}"
                  </span>
                ))}
              </div>
            </div>
          )}

          {avoid.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 mb-2">
                <ShieldAlert className="w-3.5 h-3.5" />
                Topics to avoid (already covered)
              </div>
              <ul className="space-y-1">
                {avoid.map((a, i) => (
                  <li key={i} className="text-sm text-gray-600">
                    {a.topic}
                    {a.duplicate_of_url && (
                      <a href={a.duplicate_of_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 ml-1.5 text-xs break-all">
                        {a.duplicate_of_url}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
